/**
 * Map GET /growth-engine/venues/:venueId/recommendations onto UI fields.
 *
 * Empty state, business case, first-success rule, next action, walkthrough,
 * and suggested data come from Express (feature registry). The dashboard
 * must not invent a journey from featureId.
 */

import {
  isDashboardAppOnlyCtaAction,
  resolveGrowthEngineCtaPath,
  STANDING_ORDERS_DASHBOARD_HONEST_NOTE,
} from './growthEngineCtaRoutes'
import {
  GROWTH_ENGINE_CATALOGUE_HANDOFF_PAR,
  GROWTH_ENGINE_CATALOGUE_HANDOFF_RECIPES,
  appendGrowthEngineCatalogueProductGate,
} from './growthEngineOneJobPage'
import { isInternalOrFixtureTitle } from './growthEngineInternalTitle'
import { mapGrowthEngineManagerOnePager } from './growthEngineManagerOnePager'
import { formatPurchaseOrderLabel } from './purchaseOrderLabel'

const asTrimmedString = value => {
  if (value == null) return ''
  return String(value).trim()
}

const mapCta = cta => {
  if (!cta || typeof cta !== 'object') return null
  const label = asTrimmedString(cta.label)
  const action = asTrimmedString(cta.action)
  if (!label && !action) return null
  return {
    ...(label ? { label } : {}),
    ...(action ? { action } : {}),
  }
}

const mapEmptyState = emptyState => {
  if (!emptyState || typeof emptyState !== 'object') return null
  const title = asTrimmedString(emptyState.title)
  const body = asTrimmedString(emptyState.body)
  const cta = mapCta(emptyState.cta)
  if (!title && !body && !cta) return null
  return {
    title,
    body,
    ...(emptyState.valueLine
      ? { valueLine: asTrimmedString(emptyState.valueLine) }
      : {}),
    ...(emptyState.estimatedMinutes != null
      ? { estimatedMinutes: emptyState.estimatedMinutes }
      : {}),
    ...(cta ? { cta } : {}),
  }
}

const mapBusinessCase = businessCase => {
  if (!businessCase || typeof businessCase !== 'object') return null
  const title = asTrimmedString(businessCase.title)
  const body = asTrimmedString(businessCase.body)
  if (!title && !body) return null
  return {
    title,
    body,
    ...(businessCase.valueLine
      ? { valueLine: asTrimmedString(businessCase.valueLine) }
      : {}),
    ...(businessCase.estimatedMinutes != null
      ? { estimatedMinutes: businessCase.estimatedMinutes }
      : {}),
  }
}

const mapFirstSuccess = firstSuccess => {
  if (!firstSuccess || typeof firstSuccess !== 'object') return null
  const id = asTrimmedString(firstSuccess.id)
  const completionRule = asTrimmedString(firstSuccess.completionRule)
  if (!id && !completionRule) return null
  return {
    id: id || null,
    completionRule: completionRule || null,
    ...(firstSuccess.outcome
      ? { outcome: asTrimmedString(firstSuccess.outcome) }
      : {}),
  }
}

const mapNextAction = nextAction => {
  if (!nextAction || typeof nextAction !== 'object') return null
  const intentId = asTrimmedString(nextAction.intentId)
  if (!intentId) return null
  return {
    when: asTrimmedString(nextAction.when) || 'after_first_success',
    intentId,
    ...(nextAction.summary
      ? { summary: asTrimmedString(nextAction.summary) }
      : {}),
    ...(nextAction.afterCount != null
      ? { afterCount: nextAction.afterCount }
      : {}),
    ...(mapCta(nextAction.cta) ? { cta: mapCta(nextAction.cta) } : {}),
  }
}

/**
 * Outcome + one next step after first success. Do not invent copy from featureId.
 */
export function getFirstSuccessCompletion(guidedSuccess) {
  const outcome = asTrimmedString(guidedSuccess?.firstSuccess?.outcome)
  const next = guidedSuccess?.nextAction
  const nextSummary = asTrimmedString(next?.summary)
  const cta = mapCta(next?.cta)
  if (!outcome && !nextSummary && !cta) return null
  const managerOnePager = mapGrowthEngineManagerOnePager(
    guidedSuccess?.managerOnePager,
  )
  return {
    outcome: outcome || null,
    nextSummary: nextSummary || null,
    ctaLabel: cta?.label || null,
    ctaAction: cta?.action || null,
    intentId: next?.intentId || null,
    ...(managerOnePager ? { managerOnePager } : {}),
  }
}

export function shouldShowFirstSuccessCompletion({
  justAchieved = false,
  guidedSuccess,
} = {}) {
  if (!justAchieved) return false
  return Boolean(getFirstSuccessCompletion(guidedSuccess)?.outcome)
}

const mapWalkthroughStep = step => {
  if (!step || typeof step !== 'object') return null
  const id = asTrimmedString(step.id)
  const title = asTrimmedString(step.title)
  if (!id || !title) return null
  return {
    id,
    title,
    ...(step.body ? { body: asTrimmedString(step.body) } : {}),
    ...(step.target ? { target: asTrimmedString(step.target) } : {}),
  }
}

const mapWalkthroughProgress = progress => {
  if (!progress || typeof progress !== 'object') return null
  const completedStepIds = Array.isArray(progress.completedStepIds)
    ? progress.completedStepIds.map(asTrimmedString).filter(Boolean)
    : []
  return {
    status: asTrimmedString(progress.status) || 'not_started',
    currentStepId: asTrimmedString(progress.currentStepId) || null,
    resumeStepId: asTrimmedString(progress.resumeStepId) || null,
    completedStepIds,
    startedAt: progress.startedAt ?? null,
    skippedAt: progress.skippedAt ?? null,
    cooledUntil: progress.cooledUntil ?? null,
  }
}

const mapWalkthrough = walkthrough => {
  if (!walkthrough || typeof walkthrough !== 'object') return null
  const steps = (Array.isArray(walkthrough.steps) ? walkthrough.steps : [])
    .map(mapWalkthroughStep)
    .filter(Boolean)
  if (!steps.length) return null
  return {
    steps,
    progress: mapWalkthroughProgress(walkthrough.progress) || {
      status: 'not_started',
      currentStepId: steps[0].id,
      resumeStepId: null,
      completedStepIds: [],
      startedAt: null,
      skippedAt: null,
      cooledUntil: null,
    },
  }
}

const mapSuggestedData = suggestedData => {
  if (!suggestedData || typeof suggestedData !== 'object') {
    return { source: 'none', items: [] }
  }
  return {
    source: asTrimmedString(suggestedData.source) || 'none',
    items: Array.isArray(suggestedData.items) ? suggestedData.items : [],
  }
}

/**
 * @param {object|null|undefined} recommendation
 */
export function mapGrowthEngineRecommendation(recommendation) {
  if (!recommendation || typeof recommendation !== 'object') return null
  const intentId = asTrimmedString(recommendation.intentId)
  if (!intentId) return null
  return {
    intentId,
    featureId: asTrimmedString(recommendation.featureId) || null,
    featureName: asTrimmedString(recommendation.featureName) || null,
    title: asTrimmedString(recommendation.title),
    body: asTrimmedString(recommendation.body),
    progress: recommendation.progress || null,
    cta: mapCta(recommendation.cta),
    variantIndex:
      recommendation.variantIndex == null
        ? null
        : Number(recommendation.variantIndex),
    stageId: asTrimmedString(recommendation.stageId) || null,
  }
}

/**
 * @param {object|null|undefined} guidedSuccess
 */
export function mapGrowthEngineGuidedSuccess(guidedSuccess) {
  if (!guidedSuccess || typeof guidedSuccess !== 'object') return null
  const featureId = asTrimmedString(guidedSuccess.featureId)
  if (!featureId) return null
  return {
    featureId,
    featureName: asTrimmedString(guidedSuccess.featureName) || null,
    emptyState: mapEmptyState(guidedSuccess.emptyState),
    businessCase: mapBusinessCase(guidedSuccess.businessCase),
    firstSuccess: mapFirstSuccess(guidedSuccess.firstSuccess),
    nextAction: mapNextAction(guidedSuccess.nextAction),
    walkthrough: mapWalkthrough(guidedSuccess.walkthrough),
    suggestedData: mapSuggestedData(guidedSuccess.suggestedData),
    managerOnePager: mapGrowthEngineManagerOnePager(
      guidedSuccess.managerOnePager,
    ),
  }
}

/**
 * @param {object} [response] Express GET recommendations body
 */
export function mapGrowthEngineRecommendationPayload(response = {}) {
  const guidedSuccess = mapGrowthEngineGuidedSuccess(response.guidedSuccess)
  return {
    recommendation: mapGrowthEngineRecommendation(response.recommendation),
    reason: response.reason ?? null,
    quiet: response.quiet ?? null,
    guidedSuccess,
    plan: response.plan ?? null,
    platform: response.platform || 'dashboard',
    ladder: response.ladder || null,
  }
}

/** Show first-try chrome only when Express sent an empty state. */
export function shouldShowGuidedEmptyState(guidedSuccess) {
  return Boolean(
    guidedSuccess?.emptyState?.title || guidedSuccess?.emptyState?.body,
  )
}

/**
 * Recipes list: upgrade the existing empty block when there are 0 recipes
 * and Express sent a first-try empty state. Has-recipes always uses the list.
 */
export function shouldShowRecipesGuidedEmpty({
  recipes,
  guidedSuccess,
  loading = false,
} = {}) {
  if (loading) return false
  const hasRecipes = Array.isArray(recipes) && recipes.length > 0
  if (hasRecipes) return false
  return shouldShowGuidedEmptyState(guidedSuccess)
}

export const RECIPES_NEED_PRODUCT_CTA_LABEL = 'Add a product you already buy'
export const RECIPES_COST_FIRST_DISH_CTA_LABEL = 'Cost your first dish'
export const RECIPES_NEED_PRODUCT_BODY =
  'Recipes use shopping-list products. Add one you already buy from the catalogue (or add a supplier first), then come back to cost the dish.'
export const RECIPES_NEED_PRODUCT_HONEST_NOTE =
  'Save stays disabled until one shopping-list product is on the recipe.'

export function recipesEmptyHasShoppingListProduct(products = []) {
  return (
    Array.isArray(products) &&
    products.some(product => product && (product._id || product.title))
  )
}

/** Chrome Create New Recipe: hide until they can put a product on the dish. */
export function canShowRecipesCreateChrome(products = []) {
  return recipesEmptyHasShoppingListProduct(products)
}

/**
 * 0 shopping-list products: first job is add one (catalogue / supplier).
 * After one product, Cost your first dish is the CTA.
 */
export function getRecipesEmptyCta({
  products,
  emptyStateCta,
  hasSuppliers = true,
} = {}) {
  if (recipesEmptyHasShoppingListProduct(products)) {
    const label =
      asTrimmedString(emptyStateCta?.label) || RECIPES_COST_FIRST_DISH_CTA_LABEL
    const action = asTrimmedString(emptyStateCta?.action) || 'open_menu_builder'
    return {
      label,
      action,
      needsProductFirst: false,
      honestNote: null,
    }
  }
  return {
    label: RECIPES_NEED_PRODUCT_CTA_LABEL,
    action: hasSuppliers ? 'open_catalogue' : 'add_supplier',
    needsProductFirst: true,
    honestNote: RECIPES_NEED_PRODUCT_HONEST_NOTE,
  }
}

export function getRecipesEmptyCopy({
  products,
  emptyState,
  hasSuppliers = true,
} = {}) {
  const cta = getRecipesEmptyCta({
    products,
    emptyStateCta: emptyState?.cta,
    hasSuppliers,
  })
  if (!cta.needsProductFirst) {
    return {
      title: emptyState?.title || '',
      body: emptyState?.body || '',
      valueLine: emptyState?.valueLine || null,
      estimatedMinutes: emptyState?.estimatedMinutes ?? null,
      ...cta,
    }
  }
  return {
    title: emptyState?.title || 'You have not costed a dish yet',
    body: RECIPES_NEED_PRODUCT_BODY,
    valueLine: emptyState?.valueLine || null,
    estimatedMinutes: emptyState?.estimatedMinutes ?? null,
    ...cta,
  }
}

export function resolveRecipesNeedProductPath(
  venueId,
  { hasSuppliers = true } = {},
) {
  const path = resolveGrowthEngineCtaPath(
    venueId,
    hasSuppliers ? 'open_catalogue' : 'add_supplier',
  )
  if (!hasSuppliers) return path
  return appendGrowthEngineCatalogueProductGate(
    path,
    GROWTH_ENGINE_CATALOGUE_HANDOFF_RECIPES,
  )
}

export function resolveParNeedProductPath(
  venueId,
  { hasSuppliers = true } = {},
) {
  const path = resolveGrowthEngineCtaPath(
    venueId,
    hasSuppliers ? 'open_catalogue' : 'add_supplier',
  )
  if (!hasSuppliers) return path
  return appendGrowthEngineCatalogueProductGate(
    path,
    GROWTH_ENGINE_CATALOGUE_HANDOFF_PAR,
  )
}

export const PAR_NEED_PRODUCT_CTA_LABEL = 'Add a product you already buy'
export const PAR_SET_FIRST_CTA_LABEL = 'Set a par level'
export const PAR_NEED_PRODUCT_BODY =
  'Pars live on shopping-list products. Add one you already buy from the catalogue (or add a supplier first), then come back to set the par.'
export const PAR_NEED_PRODUCT_HONEST_NOTE =
  'You cannot set a par until there is a shopping-list line.'

export function parLevelsHasShoppingListProduct(products = []) {
  return (
    Array.isArray(products) &&
    products.some(product => product && (product._id || product.title))
  )
}

/**
 * 0 shopping-list products: first job is add one (catalogue / supplier).
 * After one product, Set a par level is the CTA.
 */
export function getParLevelsEmptyCta({
  products,
  emptyStateCta,
  hasSuppliers = true,
} = {}) {
  if (parLevelsHasShoppingListProduct(products)) {
    const label =
      asTrimmedString(emptyStateCta?.label) || PAR_SET_FIRST_CTA_LABEL
    const action = asTrimmedString(emptyStateCta?.action) || 'open_par_levels'
    return {
      label,
      action,
      needsProductFirst: false,
      honestNote: null,
    }
  }
  return {
    label: PAR_NEED_PRODUCT_CTA_LABEL,
    action: hasSuppliers ? 'open_catalogue' : 'add_supplier',
    needsProductFirst: true,
    honestNote: PAR_NEED_PRODUCT_HONEST_NOTE,
  }
}

export function getParLevelsEmptyCopy({
  products,
  emptyState,
  hasSuppliers = true,
} = {}) {
  const cta = getParLevelsEmptyCta({
    products,
    emptyStateCta: emptyState?.cta,
    hasSuppliers,
  })
  if (!cta.needsProductFirst) {
    return {
      title: emptyState?.title || '',
      body: emptyState?.body || '',
      valueLine: emptyState?.valueLine || null,
      estimatedMinutes: emptyState?.estimatedMinutes ?? null,
      ...cta,
    }
  }
  return {
    title: emptyState?.title || 'No par levels yet',
    body: PAR_NEED_PRODUCT_BODY,
    valueLine: emptyState?.valueLine || null,
    estimatedMinutes: emptyState?.estimatedMinutes ?? null,
    ...cta,
  }
}

/**
 * Stock list: upgrade the existing empty block when there are 0 stock takes
 * and Express sent a first-try empty state. Has-takes always uses the list.
 */
export function shouldShowStockTakesGuidedEmpty({
  stockTakes,
  guidedSuccess,
  loading = false,
} = {}) {
  if (loading) return false
  const hasStockTakes = Array.isArray(stockTakes) && stockTakes.length > 0
  if (hasStockTakes) return false
  return shouldShowGuidedEmptyState(guidedSuccess)
}

/**
 * On 0 takes, the why is the first screen. Permissions / Main vs Partial /
 * Create sit below that block until they have a take.
 */
export function shouldLiftStockTakesEmptyAboveFold(args) {
  return shouldShowStockTakesGuidedEmpty(args)
}

/**
 * Products / par UI: first-try empty when there are 0 pars.
 * 0 shopping-list products still shows the why (catalogue gate), not No Data.
 * Has-pars keeps the table only.
 */
export function shouldShowParLevelsGuidedEmpty({
  products,
  guidedSuccess,
  loading = false,
} = {}) {
  if (loading) return false
  if (!Array.isArray(products)) return false
  const hasPars = products.some(product => {
    const n = Number(product?.parLevel)
    return Number.isFinite(n) && n > 0
  })
  if (hasPars) return false
  return shouldShowGuidedEmptyState(guidedSuccess)
}

/** Typical order qty from Express, else the product's current par. */
export function suggestedParLevelEditValue(item, product) {
  const typical = Number(item?.typicalQuantity)
  if (Number.isFinite(typical) && typical > 0) return String(typical)
  const current = Number(product?.parLevel)
  if (Number.isFinite(current) && current > 0) return String(current)
  return '0'
}

/**
 * Invoice library: upgrade the existing empty when there are 0 uploads
 * and Express sent a first-try case. Archive / filter / needs-review
 * empties stay as they are.
 */
export function shouldShowInvoiceUploadsGuidedEmpty({
  hasInvoices,
  guidedSuccess,
  loading = false,
  isArchiveView = false,
  isNeedsAttentionFilter = false,
  hasActiveFilters = false,
} = {}) {
  if (loading) return false
  if (isArchiveView || isNeedsAttentionFilter || hasActiveFilters) return false
  if (hasInvoices) return false
  return shouldShowGuidedEmptyState(guidedSuccess)
}

import { shouldShowGoodsReceiptingRangeEmpty } from './growthEngineGoodsReceiptingRangeEmpty'

/**
 * Goods receipting: upgrade the existing empty when there are 0 orders
 * and Express sent a first-try case. Filter empties stay as they are.
 * POs outside the selected dates are not “unused receipting”.
 */
export function shouldShowGoodsReceiptingGuidedEmpty({
  orders,
  guidedSuccess,
  loading = false,
  atLimit = false,
  hasPurchaseOrders = false,
  canWidenRange = false,
} = {}) {
  if (loading) return false
  if (atLimit) return false
  if (
    shouldShowGoodsReceiptingRangeEmpty({
      orders,
      hasPurchaseOrders,
      canWiden: canWidenRange,
      loading,
    })
  ) {
    return false
  }
  const hasOrders = Array.isArray(orders) && orders.length > 0
  if (hasOrders) return false
  if (hasPurchaseOrders) return false
  return shouldShowGuidedEmptyState(guidedSuccess)
}

/**
 * Restricted suppliers: upgrade in place when they have suppliers but
 * none are restricted, and Express sent a first-try case.
 * Already-restricted keeps the table only. No-suppliers keeps the list.
 */
export function shouldShowRestrictedSuppliersGuidedEmpty({
  venueSuppliers,
  guidedSuccess,
  loading = false,
} = {}) {
  if (loading) return false
  if (!Array.isArray(venueSuppliers) || venueSuppliers.length === 0) {
    return false
  }
  const hasRestricted = venueSuppliers.some(
    supplier => supplier?.restricted === true,
  )
  if (hasRestricted) return false
  return shouldShowGuidedEmptyState(guidedSuccess)
}

/** Chip label: product / supplier title, or a PO number. */
export function guidedEmptySuggestedLabel(item) {
  const title = asTrimmedString(item?.title)
  if (title && !isInternalOrFixtureTitle(title)) return title
  return formatPurchaseOrderLabel(item?.purchaseOrderNumber)
}

export function findSuggestedReceiptingOrder(orders, item) {
  if (!item || !Array.isArray(orders) || !orders.length) return null
  const po = asTrimmedString(item.purchaseOrderNumber)
  if (!po) return null
  const supplierId = asTrimmedString(item.supplierId)
  const matches = orders.filter(
    order => asTrimmedString(order?.purchaseOrderNumber) === po,
  )
  if (supplierId) {
    const bySupplier = matches.find(
      order =>
        asTrimmedString(order?.supplier?._id || order?.supplier) === supplierId,
    )
    if (bySupplier) return bySupplier
  }
  return matches[0] || null
}

export const GOODS_RECEIPTING_NEED_ORDER_CTA_LABEL =
  'Add or place an order first'

export const GOODS_RECEIPTING_RECEIPT_CTA_LABEL = 'Receipt a delivery'

/** True when the list or a suggested chip has a PO that can be receipted. */
export function goodsReceiptingEmptyHasPoToReceipt({
  orders = [],
  suggestedItems = [],
} = {}) {
  const hasOrder =
    Array.isArray(orders) &&
    orders.some(order => asTrimmedString(order?.purchaseOrderNumber))
  if (hasOrder) return true
  return (
    Array.isArray(suggestedItems) &&
    suggestedItems.some(item => asTrimmedString(item?.purchaseOrderNumber))
  )
}

/**
 * Guided empty CTA: receipt when there is a PO; otherwise send them to
 * Orders / manual add — never a dead "Receipt a delivery" click.
 */
export function getGoodsReceiptingEmptyCta({
  orders,
  suggestedItems,
  emptyStateCta,
} = {}) {
  if (goodsReceiptingEmptyHasPoToReceipt({ orders, suggestedItems })) {
    const label =
      asTrimmedString(emptyStateCta?.label) ||
      GOODS_RECEIPTING_RECEIPT_CTA_LABEL
    const action =
      asTrimmedString(emptyStateCta?.action) || 'open_goods_receipting'
    return { label, action, needsOrderFirst: false }
  }
  return {
    label: GOODS_RECEIPTING_NEED_ORDER_CTA_LABEL,
    action: 'add_or_place_order',
    needsOrderFirst: true,
  }
}

export function resolveGoodsReceiptingNeedOrderPath(venueId) {
  return resolveGrowthEngineCtaPath(venueId, 'add_or_place_order')
}

export function findSuggestedVenueSupplier(venueSuppliers, suggested) {
  if (!suggested || !Array.isArray(venueSuppliers) || !venueSuppliers.length) {
    return null
  }
  const venueSupplierId = asTrimmedString(suggested.venueSupplierId)
  if (venueSupplierId) {
    const byId = venueSuppliers.find(
      supplier => asTrimmedString(supplier?._id) === venueSupplierId,
    )
    if (byId) return byId
  }
  const supplierId = asTrimmedString(suggested.supplierId)
  if (supplierId) {
    const bySupplier = venueSuppliers.find(
      supplier =>
        asTrimmedString(supplier?.supplier?._id || supplier?.supplier) ===
        supplierId,
    )
    if (bySupplier) return bySupplier
  }
  const title = asTrimmedString(suggested.title).toLowerCase()
  if (!title) return null
  return (
    venueSuppliers.find(
      supplier =>
        asTrimmedString(
          supplier?.supplierTitle || supplier?.title,
        ).toLowerCase() === title,
    ) || null
  )
}

/** Default main-count window when they have not picked dates yet. */
export function defaultStockTakeCreatePeriod(now = new Date()) {
  const date = now instanceof Date ? now : new Date(now)
  if (Number.isNaN(date.getTime())) {
    return defaultStockTakeCreatePeriod(new Date())
  }
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const isoDay = `${year}-${month}-${day}`
  return [isoDay, isoDay]
}

/** @param {number|string|null|undefined} minutes */
export function formatGuidedEmptyMinutes(minutes) {
  const n = Number(minutes)
  if (!Number.isFinite(n) || n <= 0) return null
  return n === 1 ? 'About 1 minute' : `About ${n} minutes`
}

/**
 * Dashboard cannot create standing orders. Honest empty: why from Express,
 * no fake create CTA — tell them to do it in the app.
 */
export function getStandingOrdersDashboardEmptyProps(
  guidedSuccess,
  recommendation = null,
) {
  const featureId =
    guidedSuccess?.featureId || recommendation?.featureId || null
  const action =
    recommendation?.cta?.action ||
    guidedSuccess?.emptyState?.cta?.action ||
    null
  if (featureId !== 'standing_orders' && !isDashboardAppOnlyCtaAction(action)) {
    return null
  }

  const emptyState = guidedSuccess?.emptyState
  const title = emptyState?.title || recommendation?.title || ''
  const body = emptyState?.body || recommendation?.body || ''
  if (!title && !body) return null

  return {
    title,
    body,
    valueLine: emptyState?.valueLine || null,
    estimatedMinutes: emptyState?.estimatedMinutes ?? null,
    honestNote: STANDING_ORDERS_DASHBOARD_HONEST_NOTE,
    ctaLabel: null,
    ctaAction: null,
  }
}

/** Recipes (or any future feature) resume from server progress — not localStorage. */
export function walkthroughResumeStepId(guidedSuccess) {
  const progress = guidedSuccess?.walkthrough?.progress
  if (!progress) return null
  return progress.resumeStepId || progress.currentStepId || null
}
